import { FC, useCallback, useEffect } from 'react';
import { Button, ButtonStyles, Popup, showAlert } from '@silverfort/sf-components';
import { useApplicationsContext } from './context/ApplicationsContext';
import { useDeleteApplication } from '../../../api/mutations/useDeleteApplication';
import { useDeleteApplicationContext } from './context/DeleteApplicationContext';
import { Application } from '../../../types/Application';

const DeleteApplicationPopupContent: FC<{ application: Application }> = ({ application }) => {
    return (
        <span className="font-13">
            Are you sure you want to delete <span className="font-sf font-bold">{application.name}</span>? This action
            cannot be undone.
        </span>
    );
};

const DeleteApplicationPopupFooter: FC<{ onCancelClick: () => void; onDeleteClick: () => void }> = ({
    onCancelClick,
    onDeleteClick
}) => {
    return (
        <div className="flex flex-1 justify-end gap-2 ">
            <Button buttonStyle={ButtonStyles.GhostSolid} text="Cancel" onClick={onCancelClick} />
            <Button buttonStyle={ButtonStyles.PrimarySolid} text="Delete" onClick={onDeleteClick} />
        </div>
    );
};

export const DeleteApplicationPopup: FC = () => {
    const { refetchApplications } = useApplicationsContext();
    const { applicationToDelete, setApplicationToDelete, setDeleteApplicationLoading } = useDeleteApplicationContext();

    const closePopup = useCallback(() => {
        setApplicationToDelete(null);
    }, [setApplicationToDelete]);

    const handleSuccess = useCallback(() => {
        showAlert('Deleted the application successfully', 'success');

        refetchApplications();
        closePopup();
    }, [closePopup, refetchApplications]);

    const handleError = useCallback(() => {
        showAlert('Failed to delete the application, please try again', 'error');

        closePopup();
    }, [closePopup]);

    const { deleteApplication, deleteApplicationLoading } = useDeleteApplication({
        onSuccess: handleSuccess,
        onError: handleError
    });

    const deleteApp = useCallback(() => {
        if (applicationToDelete?.app_id) {
            deleteApplication(applicationToDelete.app_id);
        } else {
            closePopup();
        }
    }, [applicationToDelete, closePopup, deleteApplication]);

    useEffect(() => {
        setDeleteApplicationLoading(deleteApplicationLoading);
    }, [deleteApplicationLoading, setDeleteApplicationLoading]);

    return applicationToDelete ? (
        <Popup
            ContentComponent={DeleteApplicationPopupContent}
            ContentComponentProps={{ application: applicationToDelete }}
            FooterComponent={DeleteApplicationPopupFooter}
            FooterComponentProps={{
                onCancelClick: closePopup,
                onDeleteClick: deleteApp
            }}
            onClose={closePopup}
            open={Boolean(applicationToDelete)}
            popupClassName="!w-[360px] h-[240px]"
            title="Delete Application"
        />
    ) : null;
};
